/**
 * Kennzahlen des Betriebs- und Anlagevermögens — die VERSICHERUNGSSICHT.
 *
 * Sie weicht bewusst vom Anlagevermögen der Buchhaltung ab: Die EDV steht in der
 * Buchhaltung meist bei der Büro- und Geschäftsausstattung, für die Versicherung
 * gehört sie zur Elektronik. Mietereinbauten, Kundeneigentum oder geleaste
 * Maschinen tauchen im Anlagenverzeichnis fast nie auf — versichert werden
 * müssen sie trotzdem. Jede Position des Risiken-Reiters wird genau EINER
 * Kennzahl (einem Blatt dieses Baums) zugeordnet; die Gruppen summieren ihre
 * Blätter.
 *
 * Gliederung aus der Vorgabe vom 08.10.2026. Die `id`s sind der stabile
 * Schlüssel; gespeichert wird am Risikoobjekt das Merkmal „Kennzahl" mit dem
 * LABEL des Blatts (lesbar in jeder Merkmalsliste). Fehlt das Merkmal, wird
 * aus der „Vermögensart" abgeleitet — so landen auch Altbestand und
 * Anlagen-Importe ohne Nacharbeit in der richtigen Zeile.
 *
 * React-frei, damit Zuordnung und Summen ohne DOM prüfbar bleiben.
 */

export interface KennzahlKnoten {
  id: string;
  label: string;
  /** Kurze Erläuterung für die Besprechung — erscheint als Tooltip. */
  hinweis?: string;
  /** Fett hervorgehoben, ohne Gruppe zu sein (Maschinen stationär / mobil). */
  fett?: boolean;
  /**
   * Gruppe, die SELBST Positionen tragen kann: „Maschine" hat die
   * Maschinensteuerung als Unterpunkt, eine Maschine ohne getrennt erfasste
   * Steuerung steht aber direkt auf „Maschine".
   */
  eigenerWert?: boolean;
  /**
   * Gespeicherter Wert des Merkmals „Kennzahl", wenn das Label nicht eindeutig
   * ist: „Maschinen" und „Maschinensteuerung" gibt es stationär UND mobil.
   */
  merkmal?: string;
  kinder?: KennzahlKnoten[];
}

export const KENNZAHL_MERKMAL = "Kennzahl";

/** Gliederung Stand 12.10.2026 (Vorgaben vom 08., 11. und 12.10.2026). */
export const VERMOEGENS_KENNZAHLEN: KennzahlKnoten[] = [
  {
    id: "be_kfm",
    label: "Betriebseinrichtung kaufmännisch",
    hinweis: "Büro- und Ladeneinrichtung, Mobiliar, Regale — ohne Elektronik",
  },
  {
    id: "be_tech",
    label: "Betriebseinrichtung technisch",
    kinder: [
      {
        id: "bt_sonst",
        label: "Sonstige technische Betriebseinrichtung (ohne EDV und Maschinen)",
      },
      {
        id: "el",
        label: "Elektronik",
        fett: true,
        hinweis:
          "Inklusive EDV — die in der Buchhaltung meist unter Büro- und Geschäftsausstattung steht",
        kinder: [
          { id: "el_buero", label: "Büro- und Kommunikationstechnik" },
          { id: "el_edv", label: "EDV- und Informationstechnik" },
          { id: "el_konferenz", label: "Konferenz-, Schulungs-, Bild- und Tontechnik" },
          { id: "el_sicherung", label: "Sicherungs- und Meldetechnik" },
          {
            id: "el_msr",
            label: "Mess-, Steuer- und Regeltechnik ohne Maschinen",
            hinweis: "Maschinensteuerungen stehen bei der Maschine",
          },
          { id: "el_medizin", label: "Medizintechnik" },
          { id: "el_sonst", label: "Sonstige elektronische und technische Geräte" },
          { id: "el_software", label: "Software" },
        ],
      },
      {
        id: "ms",
        label: "Maschinen stationär (pauschal)",
        fett: true,
        kinder: [
          {
            id: "ms_maschine",
            label: "Maschinen stationär",
            eigenerWert: true,
            kinder: [
              {
                id: "ms_steuerung",
                label: "Maschinensteuerung",
                merkmal: "Maschinensteuerung stationär",
              },
            ],
          },
          { id: "ms_werkzeug", label: "Maschinenwerkzeuge und Zubehör" },
        ],
      },
      {
        id: "m_mobil",
        label: "Maschinen mobil sowie selbstfahrende Arbeitsmaschinen (pauschal)",
        fett: true,
        hinweis: "z. B. Radlader und Gabelstapler",
        kinder: [
          {
            id: "mm_maschine",
            label: "Maschinen mobil sowie selbstfahrende Arbeitsmaschinen",
            eigenerWert: true,
            kinder: [
              {
                id: "mm_werkzeug",
                label: "Maschinenwerkzeuge und Zubehör",
                merkmal: "Maschinenwerkzeuge und Zubehör mobil",
              },
            ],
          },
        ],
      },
    ],
  },
  {
    id: "ed_kunden",
    label: "Kundeneigentum",
    hinweis:
      "Sachen von Kunden im Betrieb — weder technische Betriebseinrichtung noch geleast oder gemietet",
  },
  {
    id: "ed_mitarbeiter",
    label: "Mitarbeitereigentum",
    hinweis: "Steht in keinem Anlagenverzeichnis — haftet der Betrieb, muss es versichert sein",
  },
  {
    id: "fremdgrund",
    label: "Ins eigene oder fremde Gebäude eingebrachte Sachen",
    hinweis: "z. B. Mietereinbauten in gemieteten Räumen",
  },
  {
    id: "wv",
    label: "Waren und Vorräte",
    kinder: [
      { id: "wv_rhb", label: "Roh-, Hilfs- und Betriebsstoffe" },
      { id: "wv_erz", label: "Unfertige, halbfertige und fertige Erzeugnisse" },
      { id: "wv_handel", label: "Handelswaren" },
      { id: "wv_sonst", label: "Sonstige Waren und Vorräte" },
    ],
  },
  {
    id: "bargeld",
    label: "Bargeld und sonstige Wertsachen",
    kinder: [
      { id: "bargeld_bar", label: "Bargeld" },
      { id: "bargeld_wert", label: "Sonstige Wertsachen" },
    ],
  },
];

/** Zeile für Positionen, die keiner Kennzahl zugeordnet werden konnten. */
export const OHNE_KENNZAHL = "ohne";

export interface KennzahlZeile {
  knoten: KennzahlKnoten;
  tiefe: number;
  /** Ids aller übergeordneten Gruppen — zum Ausblenden beim Einklappen. */
  vorfahren: string[];
  /**
   * Alle Kennzahlen, deren Positionen in diese Zeile zählen: das Blatt selbst,
   * bei Gruppen alle Wertträger darunter (inkl. der Gruppe, wenn sie
   * `eigenerWert` hat).
   */
  blaetter: string[];
  /** Ohne Unterpunkte. */
  istBlatt: boolean;
  /** Kann selbst Positionen tragen (Blatt oder `eigenerWert`). */
  traegtWert: boolean;
}

/** Baum → flache Zeilenliste in Anzeigereihenfolge (Gruppe vor ihren Kindern). */
export const kennzahlZeilen = (
  knoten: KennzahlKnoten[] = VERMOEGENS_KENNZAHLEN,
  tiefe = 0,
  vorfahren: string[] = [],
): KennzahlZeile[] =>
  knoten.flatMap((k) => {
    const unter = k.kinder ? kennzahlZeilen(k.kinder, tiefe + 1, [...vorfahren, k.id]) : [];
    const traegtWert = !k.kinder || !!k.eigenerWert;
    const blaetter = [
      ...(traegtWert ? [k.id] : []),
      ...unter.filter((z) => z.traegtWert).map((z) => z.knoten.id),
    ];
    return [{ knoten: k, tiefe, vorfahren, blaetter, istBlatt: !k.kinder, traegtWert }, ...unter];
  });

/** Alle Kennzahlen, denen eine Position zugeordnet werden kann. */
export const WERT_KENNZAHLEN = kennzahlZeilen()
  .filter((z) => z.traegtWert)
  .map((z) => z.knoten.id);

const BLAETTER = kennzahlZeilen().filter((z) => z.traegtWert);
const norm = (s: string) => s.trim().toLowerCase();
const merkmalVon = (k: KennzahlKnoten) => k.merkmal ?? k.label;
const BLATT_NACH_LABEL = new Map(BLAETTER.map((z) => [norm(merkmalVon(z.knoten)), z.knoten.id]));

/** Eindeutiger Name — „… mobil" bei den Maschinenwerkzeugen. */
export const kennzahlLabel = (id: string): string => {
  if (id === OHNE_KENNZAHL) return "Keiner Kennzahl zugeordnet";
  const k = BLAETTER.find((z) => z.knoten.id === id)?.knoten;
  return k ? merkmalVon(k) : id;
};

/**
 * Vermögensart (Altbestand, Anlagen-Import, Handerfassung) → Kennzahl.
 * Reihenfolge zählt: „Sonstige technische Betriebseinrichtung (ohne EDV und
 * Maschinen)" nennt EDV und Maschinen und muss vor ihnen stehen; EDV vor der
 * allgemeinen Betriebsausstattung. Die frühere Kennzahl „Von Mietern
 * eingebrachte Sachen" ist entfallen — solche Positionen bleiben sichtbar ohne
 * Kennzahl.
 */
const AUS_VERMOEGENSART: [RegExp, string][] = [
  [/technische betriebs|betriebseinrichtung techn/, "bt_sonst"],
  [/mietereinbau|bauten auf fremde|fremde[nm]? grund|geb[äa]ude eingebracht/, "fremdgrund"],
  [/mitarbeiter/, "ed_mitarbeiter"],
  [/kunde/, "ed_kunden"],
  [/software|lizenz/, "el_software"],
  [/\bedv\b|\bit\b|informationstechn|server/, "el_edv"],
  [/kommunikation|telefon/, "el_buero"],
  [/sicherung|melde|alarm/, "el_sicherung"],
  [/mess-|regeltechnik/, "el_msr"],
  [/medizin/, "el_medizin"],
  [/elektron/, "el_sonst"],
  // Die mobile Maschinensteuerung ist entfallen — sie zählt zur Maschine.
  [/steuerung.*mobil|mobil.*steuerung/, "mm_maschine"],
  [/steuerung/, "ms_steuerung"],
  [/werkzeug.*mobil|mobil.*werkzeug/, "mm_werkzeug"],
  [/werkzeug/, "ms_werkzeug"],
  [/mobil|arbeitsmaschine|stapler|radlader/, "mm_maschine"],
  [/maschine|anlage(?!n auf)/, "ms_maschine"],
  [/kaufm|geschäftsausstatt|geschaeftsausstatt|büro|buero|geringwertig|betriebseinrichtung/, "be_kfm"],
  [/rohstoff|rohmaterial|hilfsstoff|betriebsstoff|unverarbeitet/, "wv_rhb"],
  [/erzeugnis|fertigware/, "wv_erz"],
  [/handelsware/, "wv_handel"],
  [/waren|vorr[äa]t/, "wv_sonst"],
  [/bargeld|\bkasse\b|tageskasse/, "bargeld_bar"],
  [/wertsache|edelmetall|schmuck/, "bargeld_wert"],
];

/**
 * Kennzahl einer Position. Kunden- und Mitarbeitereigentum (an der
 * Eigentumsart erkannt) haben eigene Kennzahlen und gewinnen auch gegen ein
 * gesetztes Merkmal: eine Maschine im Kundeneigentum gehört nicht zu den
 * Maschinen des Betriebs (Vorgabe 12.10.2026). Sonst gewinnt das ausdrückliche
 * Merkmal; danach wird aus der Vermögensart abgeleitet. `OHNE_KENNZAHL`, wenn beides nichts ergibt —
 * die Position bleibt dann sichtbar in einer eigenen Zeile, statt still aus der
 * Summe zu fallen.
 */
export const kennzahlFuer = (
  kennzahlMerkmal: string | null | undefined,
  vermoegensart: string | null | undefined,
  eigentumsart?: string | null,
): string => {
  const e = norm(eigentumsart ?? "");
  if (/kunde/.test(e)) return "ed_kunden";
  if (/mitarbeiter/.test(e)) return "ed_mitarbeiter";
  if (kennzahlMerkmal) {
    const k = norm(kennzahlMerkmal);
    const treffer = BLATT_NACH_LABEL.get(k);
    if (treffer) return treffer;
    // Frühere Bezeichnung (z. B. „Rohstoffe", „Maschinen mobil") — der Katalog
    // wird umbenannt, die Merkmale am Bestand nicht.
    for (const [re, id] of AUS_VERMOEGENSART) if (re.test(k)) return id;
  }
  const v = norm(vermoegensart ?? "");
  if (!v) return OHNE_KENNZAHL;
  const exakt = BLATT_NACH_LABEL.get(v);
  if (exakt) return exakt;
  if (/anzahlung/.test(v)) return OHNE_KENNZAHL;
  for (const [re, id] of AUS_VERMOEGENSART) if (re.test(v)) return id;
  return OHNE_KENNZAHL;
};

const blaetterVon = (id: string): string[] =>
  kennzahlZeilen().find((z) => z.knoten.id === id)?.blaetter ?? [];

/**
 * Kennzahlen OHNE Aufteilung in Eigentum / Besitz (Vorgaben 08. und 12.10.2026):
 * Kunden- und Mitarbeitereigentum gehören per Definition nie dem Betrieb,
 * und Vorräte werden nicht geleast oder gemietet. Einzige Ausnahme sind Handelswaren — dort gibt es Kommissions- und
 * Konsignationsware, die der Lieferant zur Verfügung stellt.
 */
const OHNE_AUFTEILUNG = new Set([
  "ed_mitarbeiter",
  "ed_kunden",
  ...blaetterVon("wv").filter((id) => id !== "wv_handel"),
]);

export const hatAufteilung = (kennzahl: string): boolean => !OHNE_AUFTEILUNG.has(kennzahl);

/**
 * Spalte „Im Eigentum" oder „Im Besitz" (geleast, gemietet, zur Verfügung
 * gestellt sowie Mietkauf); „ohne" bei Kennzahlen, die nicht aufgeteilt werden — sie zählen
 * nur in „Wert gesamt". „Mietereinbauten" sind Eigentum des Mieters, also des
 * Kunden selbst.
 */
export type Besitz = "eigen" | "fremd" | "ohne";

export const besitzFuer = (kennzahl: string, eigentumsart: string | null | undefined): Besitz => {
  if (!hatAufteilung(kennzahl)) return "ohne";
  const e = norm(eigentumsart ?? "");
  if (/mietereinbau/.test(e)) return "eigen";
  if (
    /leas|miet|zur verfügung|zur verfuegung|überlass|ueberlass|fremd|dritt|kommission|konsignation/.test(
      e,
    )
  )
    return "fremd";
  return "eigen";
};

export interface KennzahlPosten {
  kennzahl: string;
  besitz: Besitz;
  wert: number;
}

export interface KennzahlSumme {
  gesamt: number;
  eigen: number;
  fremd: number;
  anzahl: number;
}

/** Summe über die Blätter einer Zeile. */
export const summeFuer = <T extends KennzahlPosten>(
  posten: T[],
  blaetter: string[],
): KennzahlSumme => {
  const set = new Set(blaetter);
  const s: KennzahlSumme = { gesamt: 0, eigen: 0, fremd: 0, anzahl: 0 };
  for (const p of posten) {
    if (!set.has(p.kennzahl)) continue;
    s.gesamt += p.wert;
    if (p.besitz !== "ohne") s[p.besitz] += p.wert;
    s.anzahl += 1;
  }
  return s;
};
